// API Service - Calls VinciKit Backend API
// For Firebase production: VITE_API_URL is empty/undefined, uses relative paths (/api/*)
// For local Docker dev: VITE_API_URL=http://localhost:8080

const API_URL = import.meta.env.VITE_API_URL !== undefined ? import.meta.env.VITE_API_URL : 'http://localhost:8080';

// Get stored token
function getToken() {
  return localStorage.getItem('vincikit_token');
}

// Set token
function setToken(token) {
  if (token) {
    localStorage.setItem('vincikit_token', token);
  } else {
    localStorage.removeItem('vincikit_token');
  }
}

// Authenticated fetch helper
async function authenticatedFetch(endpoint, options = {}) {
  const token = getToken();

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Request failed' }));

    // Handle token expiration
    if (response.status === 401) {
      setToken(null);
      window.dispatchEvent(new Event('auth:logout'));
    }

    throw new Error(errorData.error || `Request failed with status ${response.status}`);
  }

  return response.json();
}

// Auth endpoints
export async function register({ email, password, displayName, referralCode }) {
  const response = await authenticatedFetch('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password, displayName, referralCode })
  });
  if (response.token) {
    setToken(response.token);
  }
  return response;
}

export async function login({ email, password }) {
  const response = await authenticatedFetch('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password })
  });
  if (response.token) {
    setToken(response.token);
  }
  return response;
}

export async function logout() {
  setToken(null);
  return { success: true };
}

// Google OAuth - exchange Firebase ID token for backend session
export async function loginWithGoogleToken(idToken, userData) {
  const response = await authenticatedFetch('/auth/google', {
    method: 'POST',
    body: JSON.stringify({ idToken, userData })
  });
  if (response.token) {
    setToken(response.token);
  }
  return response;
}

export async function getCurrentUser() {
  if (!getToken()) return null;
  try {
    const response = await authenticatedFetch('/auth/me');
    return response.user;
  } catch (error) {
    return null;
  }
}

export async function updateProfile({ displayName, photoUrl }) {
  return authenticatedFetch('/auth/profile', {
    method: 'PUT',
    body: JSON.stringify({ displayName, photoUrl })
  });
}

// API endpoints
export async function generateStackRecommendation(config) {
  return authenticatedFetch('/api/recommend', {
    method: 'POST',
    body: JSON.stringify(config)
  });
}

export async function joinWaitlist({ email, sessionId, stack, feedback }) {
  return authenticatedFetch('/api/waitlist', {
    method: 'POST',
    body: JSON.stringify({ email, sessionId, stack, feedback })
  });
}

export async function getMySessions() {
  return authenticatedFetch('/api/mySessions', {
    method: 'POST',
    body: JSON.stringify({})
  });
}

export async function getAdminData() {
  return authenticatedFetch('/api/admin', {
    method: 'POST',
    body: JSON.stringify({})
  });
}

export async function trackEvent(eventName, eventData = {}) {
  try {
    await authenticatedFetch('/api/trackEvent', {
      method: 'POST',
      body: JSON.stringify({ eventName, eventData })
    });
  } catch (error) {
    console.warn('Analytics error:', error);
  }
}

export async function getExamples() {
  try {
    return await authenticatedFetch('/api/examples', { method: 'GET' });
  } catch (error) {
    console.error('Get examples error:', error);
    return { examples: [] };
  }
}

export async function saveExamples(examples) {
  return authenticatedFetch('/api/examples', {
    method: 'POST',
    body: JSON.stringify({ examples, action: 'save' })
  });
}

export async function addExample(example) {
  return authenticatedFetch('/api/examples', {
    method: 'POST',
    body: JSON.stringify({ example, action: 'add' })
  });
}

export async function deleteExample(exampleId) {
  return authenticatedFetch('/api/examples', {
    method: 'POST',
    body: JSON.stringify({ exampleId, action: 'delete' })
  });
}

export async function manageAdmins(action, email) {
  return authenticatedFetch('/api/manageAdmins', {
    method: 'POST',
    body: JSON.stringify({ action, email })
  });
}

export async function getTransfer(token) {
  return authenticatedFetch(`/api/getTransfer?token=${encodeURIComponent(token)}`, {
    method: 'GET'
  });
}

export async function claimTransfer(token) {
  return authenticatedFetch('/api/claimTransfer', {
    method: 'POST',
    body: JSON.stringify({ token })
  });
}

export async function getStripePlans() {
  return authenticatedFetch('/api/stripePlans', {
    method: 'POST',
    body: JSON.stringify({ action: 'list' })
  });
}

export async function updateStripePlan(planKey, updates) {
  return authenticatedFetch('/api/stripePlans', {
    method: 'POST',
    body: JSON.stringify({ action: 'update', planKey, updates })
  });
}

export async function createStripePlan(planData) {
  return authenticatedFetch('/api/stripePlans', {
    method: 'POST',
    body: JSON.stringify({ action: 'create', planData })
  });
}

export async function syncStripePlans() {
  return authenticatedFetch('/api/stripePlans', {
    method: 'POST',
    body: JSON.stringify({ action: 'sync' })
  });
}

export async function getUserUsage() {
  return authenticatedFetch('/api/getUserUsage', {
    method: 'POST',
    body: JSON.stringify({})
  });
}

export async function applyReferral(referralCode) {
  return authenticatedFetch('/api/applyReferral', {
    method: 'POST',
    body: JSON.stringify({ referralCode })
  });
}

export async function getReferralInfo(code) {
  return authenticatedFetch(`/api/getReferralInfo?code=${encodeURIComponent(code)}`, {
    method: 'GET'
  });
}

// =====================================================
// SESSION TRACKING - For anonymous and authenticated users
// =====================================================

// Get or create anonymous ID for session tracking
export function getAnonymousId() {
  let id = localStorage.getItem('vincikit_anonymous_id');
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem('vincikit_anonymous_id', id);
  }
  return id;
}

// Create session when user submits goal (before recommendation)
export async function createSession({ sessionId, goal }) {
  try {
    return await authenticatedFetch('/api/createSession', {
      method: 'POST',
      body: JSON.stringify({ sessionId, goal, anonymousId: getAnonymousId() })
    });
  } catch (error) {
    console.warn('Create session error:', error);
    // Don't throw - session creation is non-blocking
    return { success: false, error: error.message };
  }
}

// Update session progress as user answers interview questions
export async function updateSessionProgress(sessionId, progress) {
  try {
    return await authenticatedFetch('/api/updateSessionProgress', {
      method: 'POST',
      body: JSON.stringify({ sessionId, ...progress, anonymousId: getAnonymousId() })
    });
  } catch (error) {
    console.warn('Update session progress error:', error);
    // Don't throw - progress updates are non-blocking
    return { success: false, error: error.message };
  }
}

// Link anonymous sessions to authenticated user
export async function linkAnonymousSessions() {
  const anonymousId = localStorage.getItem('vincikit_anonymous_id');
  if (!anonymousId) {
    return { success: true, linked: 0 };
  }

  try {
    return await authenticatedFetch('/api/linkAnonymousSessions', {
      method: 'POST',
      body: JSON.stringify({ anonymousId })
    });
  } catch (error) {
    console.warn('Link anonymous sessions error:', error);
    return { success: false, error: error.message };
  }
}

// Export token helpers for AuthContext
export { getToken, setToken };
